import React, { useState, useEffect } from "react";

function WarfarinCalculator() {
  const [currentINR, setCurrentINR] = useState("");
  const [currentWeeklyDose, setCurrentWeeklyDose] = useState("");
  const [targetINR, setTargetINR] = useState("2.5");
  const [availableStrengths, setAvailableStrengths] = useState([]);
  const [warfarinOutput, setWarfarinOutput] = useState({
    show: false,
    newWeeklyDose: "",
    adjustmentReason: "",
    dailyScheduleSummary: "",
    dailyScheduleList: [],
    inrWarning: "",
    showInrWarning: false,
    showBleedingWarning: false,
    specificActionMessage: "",
  });

  // Load preferences from localStorage on component mount
  useEffect(() => {
    loadWarfarinPreferences();
  }, []);

  // Save preferences whenever they change
  useEffect(() => {
    if (availableStrengths.length > 0) {
      // Only save if we have loaded the initial state
      saveWarfarinPreferences();
    }
  }, [availableStrengths, targetINR]);

  const loadWarfarinPreferences = () => {
    // Load available strengths with default fallback
    let savedStrengths = null;
    try {
      const savedStrengthsStr = localStorage.getItem(
        "availableWarfarinStrengths",
      );
      if (savedStrengthsStr) {
        savedStrengths = JSON.parse(savedStrengthsStr);
      }
    } catch (error) {
      console.warn("Failed to parse saved strengths from localStorage:", error);
    }

    if (
      savedStrengths &&
      Array.isArray(savedStrengths) &&
      savedStrengths.length > 0
    ) {
      setAvailableStrengths(savedStrengths);
    } else {
      // Default to all strengths if nothing is saved or invalid
      const defaultStrengths = [1, 2, 3, 5];
      setAvailableStrengths(defaultStrengths);
      // Save the default to localStorage
      try {
        localStorage.setItem(
          "availableWarfarinStrengths",
          JSON.stringify(defaultStrengths),
        );
      } catch (error) {
        console.warn(
          "Failed to save default strengths to localStorage:",
          error,
        );
      }
    }

    // Load target INR with default fallback
    const savedTargetINR = localStorage.getItem("selectedTargetINR");
    if (savedTargetINR && ["2.0", "2.5", "3.0"].includes(savedTargetINR)) {
      setTargetINR(savedTargetINR);
    } else {
      // Default to 2.5 if nothing is saved or invalid
      setTargetINR("2.5");
      try {
        localStorage.setItem("selectedTargetINR", "2.5");
      } catch (error) {
        console.warn(
          "Failed to save default target INR to localStorage:",
          error,
        );
      }
    }
  };

  const saveWarfarinPreferences = () => {
    try {
      localStorage.setItem(
        "availableWarfarinStrengths",
        JSON.stringify(availableStrengths),
      );
      localStorage.setItem("selectedTargetINR", targetINR);
    } catch (error) {
      console.warn("Failed to save preferences to localStorage:", error);
    }
  };

  const handleStrengthChange = (strength) => {
    setAvailableStrengths((prev) => {
      if (prev.includes(strength)) {
        return prev.filter((s) => s !== strength);
      } else {
        return [...prev, strength];
      }
    });
  };

  const generateAllAchievableDailyDosesAndBreakdowns = (fullStrengths) => {
    const unitDoses = new Set();
    fullStrengths.forEach((s) => {
      unitDoses.add(s);
      unitDoses.add(s / 2);
    });
    const sortedUnitDoses = Array.from(unitDoses).sort((a, b) => a - b);

    const achievableDosesMap = new Map();
    achievableDosesMap.set(0, { dose: 0, breakdown: [] });

    const maxPillsPerDay = 4;

    for (let unit of sortedUnitDoses) {
      const currentDosesEntries = Array.from(achievableDosesMap.entries());

      for (let [prevSum, prevEntry] of currentDosesEntries) {
        const prevBreakdown = prevEntry.breakdown;

        if (prevBreakdown.length >= maxPillsPerDay) {
          continue;
        }

        const newSum = parseFloat((prevSum + unit).toFixed(1));
        const isUnitHalf = !fullStrengths.includes(unit);
        const newBreakdown = [
          ...prevBreakdown,
          { strength: unit, isHalf: isUnitHalf },
        ];

        if (
          !achievableDosesMap.has(newSum) ||
          newBreakdown.length < achievableDosesMap.get(newSum).breakdown.length
        ) {
          achievableDosesMap.set(newSum, {
            dose: newSum,
            breakdown: newBreakdown,
          });
        }
      }
    }

    return Array.from(achievableDosesMap.values()).sort(
      (a, b) => a.dose - b.dose,
    );
  };

  const getClosestBreakdownForAmount = (
    amount,
    allAchievableDailyDosesWithBreakdowns,
  ) => {
    let bestBreakdown = [];
    let minDiff = Infinity;
    if (amount === 0) return [];

    for (let entry of allAchievableDailyDosesWithBreakdowns) {
      const diff = Math.abs(entry.dose - amount);
      if (diff < minDiff) {
        minDiff = diff;
        bestBreakdown = entry.breakdown;
      }
    }
    return bestBreakdown;
  };

  const distributeWeeklyDose = (
    weeklyDose,
    allAchievableDailyDosesWithBreakdowns,
  ) => {
    const dailyDoses = Array(7).fill(null);
    let remainingDose = weeklyDose;
    let totalDistributed = 0;

    for (let i = 0; i < 7; i++) {
      let targetDayDose = remainingDose / (7 - i);
      let bestMatch = { dose: 0, breakdown: [] };
      let minDiff = Infinity;

      for (let achievableEntry of allAchievableDailyDosesWithBreakdowns) {
        const currentAchievableDose = achievableEntry.dose;
        const diff = Math.abs(currentAchievableDose - targetDayDose);

        if (currentAchievableDose <= remainingDose + 0.1) {
          if (diff < minDiff) {
            minDiff = diff;
            bestMatch = achievableEntry;
          }
        } else if (
          diff < minDiff &&
          (i === 6 || currentAchievableDose < (weeklyDose / 7) * 2)
        ) {
          minDiff = diff;
          bestMatch = achievableEntry;
        }
      }

      if (
        bestMatch.dose === 0 &&
        targetDayDose > 0 &&
        allAchievableDailyDosesWithBreakdowns.length > 1
      ) {
        const smallestNonZero = allAchievableDailyDosesWithBreakdowns.find(
          (entry) => entry.dose > 0,
        );
        if (smallestNonZero) {
          bestMatch = smallestNonZero;
        }
      }

      if (i === 6 && bestMatch.dose > remainingDose + 0.05) {
        bestMatch = {
          dose: remainingDose,
          breakdown: getClosestBreakdownForAmount(
            remainingDose,
            allAchievableDailyDosesWithBreakdowns,
          ),
        };
      }

      const actualDoseForDay = Math.max(
        0,
        parseFloat(bestMatch.dose.toFixed(1)),
      );
      dailyDoses[i] = {
        amount: actualDoseForDay,
        breakdown: bestMatch.breakdown,
      };
      remainingDose -= actualDoseForDay;
      totalDistributed += actualDoseForDay;
    }

    let finalTotal = dailyDoses.reduce((acc, val) => {
      return acc + (val && typeof val.amount === "number" ? val.amount : 0);
    }, 0);
    let overallDifference = weeklyDose - finalTotal;

    if (Math.abs(overallDifference) > 0.01) {
      const adjustmentPerDay = overallDifference / 7;
      dailyDoses.forEach((dayEntry, index) => {
        let adjustedAmount = parseFloat(
          (dayEntry.amount + adjustmentPerDay).toFixed(1),
        );
        dayEntry.amount = Math.max(0, adjustedAmount);
        if (
          Math.abs(adjustmentPerDay) > 0.01 ||
          (dayEntry.amount > 0 && dayEntry.breakdown.length === 0)
        ) {
          dayEntry.breakdown = getClosestBreakdownForAmount(
            dayEntry.amount,
            allAchievableDailyDosesWithBreakdowns,
          );
        }
      });
    }

    return {
      doses: dailyDoses,
      totalCalculated: parseFloat(
        dailyDoses
          .reduce((acc, val) => {
            return (
              acc + (val && typeof val.amount === "number" ? val.amount : 0)
            );
          }, 0)
          .toFixed(1),
      ),
    };
  };

  const calculateWarfarin = () => {
    const currentINRValue = parseFloat(currentINR);
    const currentWeeklyDoseValue = parseFloat(currentWeeklyDose);
    const targetINRValue = parseFloat(targetINR);

    if (
      isNaN(currentINRValue) ||
      isNaN(currentWeeklyDoseValue) ||
      currentINRValue <= 0 ||
      currentWeeklyDoseValue <= 0 ||
      targetINRValue === null
    ) {
      setWarfarinOutput({
        show: true,
        newWeeklyDose:
          "Please enter valid numbers for Current INR and Weekly Dose, and select a Target INR.",
        adjustmentReason: "",
        dailyScheduleSummary: "",
        dailyScheduleList: [],
        inrWarning: "",
        showInrWarning: false,
        showBleedingWarning: false,
        specificActionMessage: "",
      });
      return;
    }

    if (availableStrengths.length === 0) {
      setWarfarinOutput({
        show: true,
        newWeeklyDose: "Please select at least one available tablet strength.",
        adjustmentReason: "",
        dailyScheduleSummary: "",
        dailyScheduleList: [],
        inrWarning: "",
        showInrWarning: false,
        showBleedingWarning: false,
        specificActionMessage: "",
      });
      return;
    }

    const allAchievableDailyDosesWithBreakdowns =
      generateAllAchievableDailyDosesAndBreakdowns(availableStrengths);

    let adjustedWeeklyDose = currentWeeklyDoseValue;
    let adjustmentReason = "";
    let specificActionMessage = "";
    let showBleedingWarning = false;

    if (currentINRValue >= 9.0) {
      specificActionMessage = `INR ${currentINRValue} is extremely high. Omit current doses. Consult your doctor immediately. Vitamin K1 5-10 mg orally is often recommended.`;
      adjustmentReason = `(Critical INR - Action: Omit doses & seek medical advice)`;
      showBleedingWarning = true;
    } else if (currentINRValue >= 5.0 && currentINRValue <= 8.9) {
      specificActionMessage = `INR ${currentINRValue} is high. Omit 1-2 doses. Consult your doctor immediately. Vitamin K1 1 mg orally is often recommended.`;
      adjustmentReason = `(High INR - Action: Omit 1-2 doses & seek medical advice)`;
      showBleedingWarning = true;
    } else if (currentINRValue >= 4.0 && currentINRValue <= 4.9) {
      adjustedWeeklyDose = currentWeeklyDoseValue * 0.9;
      specificActionMessage = `INR ${currentINRValue} is moderately high. Hold Warfarin for 1 day, then consider the new weekly dose.`;
      adjustmentReason = `(Decrease by 10% after 1-day hold due to moderately high INR)`;
    } else if (currentINRValue >= 3.1 && currentINRValue <= 3.9) {
      adjustedWeeklyDose = currentWeeklyDoseValue * 0.925;
      adjustmentReason = `(Decreased by 7.5% due to INR ${currentINRValue} being above target range)`;
    } else if (currentINRValue >= 2.0 && currentINRValue <= 3.0) {
      adjustedWeeklyDose = currentWeeklyDoseValue;
      adjustmentReason = `(No change, INR ${currentINRValue} is within target range)`;
    } else if (currentINRValue >= 1.5 && currentINRValue <= 1.9) {
      adjustedWeeklyDose = currentWeeklyDoseValue * 1.075;
      adjustmentReason = `(Increased by 7.5% due to INR ${currentINRValue} being below target range)`;
    } else if (currentINRValue < 1.5) {
      adjustedWeeklyDose = currentWeeklyDoseValue * 1.15;
      specificActionMessage = `INR ${currentINRValue} is very low. This increases your risk of clotting. Please consult your doctor immediately.`;
      adjustmentReason = `(Increased by 15% due to very low INR)`;
    }

    adjustedWeeklyDose = Math.round(adjustedWeeklyDose * 2) / 2;
    adjustedWeeklyDose = parseFloat(adjustedWeeklyDose.toFixed(1));

    const dailyDoses = distributeWeeklyDose(
      adjustedWeeklyDose,
      allAchievableDailyDosesWithBreakdowns,
    );

    const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const dailyScheduleList = dailyDoses.doses.map((dailyDoseObj, index) => {
      const day = daysOfWeek[index];
      const doseAmount = dailyDoseObj.amount;
      const doseBreakdown = dailyDoseObj.breakdown;

      return {
        day,
        amount: doseAmount,
        breakdown: doseBreakdown,
      };
    });

    if (Math.abs(adjustedWeeklyDose - dailyDoses.totalCalculated) > 0.01) {
      const diff = (adjustedWeeklyDose - dailyDoses.totalCalculated).toFixed(2);
      dailyScheduleList.push({
        day: "Note",
        amount: null,
        breakdown: [],
        note: `Due to available tablet strengths, the distributed dose total is ${dailyDoses.totalCalculated.toFixed(1)} mg (difference: ${diff} mg).`,
      });
    }

    setWarfarinOutput({
      show: true,
      newWeeklyDose: `New Weekly Dose: ${adjustedWeeklyDose.toFixed(1)} mg ${adjustmentReason}`,
      adjustmentReason,
      dailyScheduleSummary: `Suggested Daily Distribution (Total: ${dailyDoses.totalCalculated.toFixed(1)} mg):`,
      dailyScheduleList,
      inrWarning: specificActionMessage,
      showInrWarning: !!specificActionMessage,
      showBleedingWarning,
      specificActionMessage,
    });
  };

  const renderPillRepresentation = (breakdown) => {
    return breakdown.map((p, index) => {
      const originalFullStrengthForColor = p.isHalf
        ? p.strength * 2
        : p.strength;
      const colorClass = `pill-color-${originalFullStrengthForColor}`;
      const halfClass = p.isHalf ? "half-pill" : "";

      return (
        <div key={index} className="pill-item">
          <div className={`pill-circle ${colorClass} ${halfClass}`}></div>
          <span className="pill-item-label">
            {p.strength}
            {p.isHalf ? " (½)" : ""}
          </span>
        </div>
      );
    });
  };

  return (
    <div id="warfarinCalculator" className="fade-in">
      <div className="warfarin-section">
        <h2 className="section-title">Warfarin Dose Adjustment</h2>

        <div className="input-group">
          <label htmlFor="currentINR">Current INR</label>
          <input
            type="number"
            id="currentINR"
            step="0.1"
            min="0"
            placeholder="e.g., 2.5"
            value={currentINR}
            onChange={(e) => setCurrentINR(e.target.value)}
          />
        </div>

        <div className="input-group">
          <label htmlFor="currentWeeklyDose">
            Current Weekly Warfarin Dose (mg)
          </label>
          <input
            type="number"
            id="currentWeeklyDose"
            step="0.1"
            min="0"
            placeholder="e.g., 35"
            value={currentWeeklyDose}
            onChange={(e) => setCurrentWeeklyDose(e.target.value)}
          />
        </div>

        <div className="input-group">
          <label>Target INR Range</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="targetINR"
                value="2.0"
                checked={targetINR === "2.0"}
                onChange={(e) => setTargetINR(e.target.value)}
              />
              <span>2.0</span>
            </label>
            <label>
              <input
                type="radio"
                name="targetINR"
                value="2.5"
                checked={targetINR === "2.5"}
                onChange={(e) => setTargetINR(e.target.value)}
              />
              <span>2.5</span>
            </label>
            <label>
              <input
                type="radio"
                name="targetINR"
                value="3.0"
                checked={targetINR === "3.0"}
                onChange={(e) => setTargetINR(e.target.value)}
              />
              <span>3.0</span>
            </label>
          </div>
        </div>

        <div className="input-group">
          <label>Available Tablet Strengths (mg)</label>
          <div className="checkbox-group">
            {[1, 2, 3, 5].map((strength) => (
              <label key={strength}>
                <input
                  type="checkbox"
                  value={strength}
                  checked={availableStrengths.includes(strength)}
                  onChange={() => handleStrengthChange(strength)}
                />
                <span>{strength}mg</span>
              </label>
            ))}
          </div>
        </div>

        <button className="calculate-button" onClick={calculateWarfarin}>
          Calculate Warfarin Dose
        </button>

        {warfarinOutput.show && (
          <div className="warfarin-output fade-in">
            {warfarinOutput.showInrWarning && (
              <p className="warning-message">{warfarinOutput.inrWarning}</p>
            )}
            <h3>Adjusted Warfarin Dose</h3>
            <p className="text-lg font-bold text-gray-800">
              {warfarinOutput.newWeeklyDose}
            </p>
            <p>{warfarinOutput.dailyScheduleSummary}</p>
            <h4>Daily Dosing Schedule</h4>
            <ul>
              {warfarinOutput.dailyScheduleList.map((item, index) => (
                <li key={index}>
                  {item.note ? (
                    <span style={{ fontStyle: "italic", color: "#6b7280" }}>
                      {item.note}
                    </span>
                  ) : (
                    <>
                      <span className="font-medium">
                        {item.day}: {item.amount.toFixed(1)} mg
                      </span>
                      <div className="pill-representation">
                        {renderPillRepresentation(item.breakdown)}
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <p className="disclaimer">
        <strong>Medical Disclaimer:</strong> This calculator is for
        demonstration purposes only and provides simplified calculations based
        on common US guidelines. It is NOT a substitute for professional medical
        advice, diagnosis, or treatment. Warfarin dosing is complex and highly
        individualized. Always consult with a qualified healthcare professional
        before making any changes to your medication regimen.
      </p>

      {warfarinOutput.showBleedingWarning && (
        <p className="disclaimer critical-warning">
          <strong>Medical Emergency Advisory:</strong> If you are experiencing
          Major Bleeding with ANY INR, this is a medical emergency. Seek
          immediate professional medical attention. Specific treatment with
          Vitamin K1 (IV) and Fresh Frozen Plasma (FFP) may be required. This
          tool cannot advise on emergencies.
        </p>
      )}
    </div>
  );
}

export default WarfarinCalculator;
